import type { CardInfo } from '@lub0v/santa-utils';

type SortableCard = Pick<CardInfo, 'id' | 'username' | 'isYou' | 'wardId'> & {
  createdAt?: number;
  sortOrder?: number;
};

function createdAt(card: SortableCard): number {
  if (typeof card.createdAt === 'number' && Number.isFinite(card.createdAt)) {
    return card.createdAt;
  }
  return card.id || 0;
}

function hasCustomOrder(cards: SortableCard[]): boolean {
  return cards.some((card) => typeof card.sortOrder === 'number');
}

/**
 * The order the organizer sees, as in v1: themselves first, then anyone still
 * without a giftee — those are the rows that need attention — and the rest in
 * the order they were added.
 */
export function sortOrganizerCards<T extends SortableCard>(
  cards: T[],
  options?: { unassignedFirst?: boolean },
): T[] {
  const custom = hasCustomOrder(cards);
  return [...cards].sort((left, right) => {
    if (left.isYou !== right.isYou) {
      return left.isYou ? -1 : 1;
    }
    if (options?.unassignedFirst) {
      const leftOpen = left.wardId ? 1 : 0;
      const rightOpen = right.wardId ? 1 : 0;
      if (leftOpen !== rightOpen) {
        return leftOpen - rightOpen;
      }
    }
    if (custom) {
      return (
        (left.sortOrder ?? Number.MAX_SAFE_INTEGER) - (right.sortOrder ?? Number.MAX_SAFE_INTEGER)
      );
    }
    const byTime = createdAt(left) - createdAt(right);
    if (byTime) {
      return byTime;
    }
    return (left.id || 0) - (right.id || 0);
  });
}

/** Shared-link name picker: free names first, taken after; each group A–Z. */
export function sortSelectCards<T extends Pick<CardInfo, 'username' | 'isVerified'>>(cards: T[]): T[] {
  return [...cards].sort((left, right) => {
    const leftTaken = left.isVerified ? 1 : 0;
    const rightTaken = right.isVerified ? 1 : 0;
    if (leftTaken !== rightTaken) {
      return leftTaken - rightTaken;
    }
    return (left.username || '').localeCompare(right.username || '', undefined, {
      sensitivity: 'base',
    });
  });
}

/** Participants see themselves first and then everyone else by name. */
export function sortParticipantCards<T extends SortableCard>(cards: T[]): T[] {
  return [...cards].sort((left, right) => {
    if (left.isYou !== right.isYou) {
      return left.isYou ? -1 : 1;
    }
    return (left.username || '').localeCompare(right.username || '', undefined, {
      sensitivity: 'base',
    });
  });
}
